/**
 * useClientConfig
 *
 * Hook para carregar configurações do tenant via API /core/config/client.
 * Cacheia em zustand store. Substitui referências a import.meta.env por config dinâmico.
 *
 * @since 2026-04-17
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/apiClient';

// =============================================================================
// Types
// =============================================================================

export interface ClientConfig {
  clientCode: string;
  clientName: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  features: Record<string, boolean>;
  settings: Record<string, unknown>;
  source: 'hub' | 'env';
}

// =============================================================================
// Store (persisted)
// =============================================================================

interface ClientConfigState {
  config: ClientConfig | null;
  isLoaded: boolean;
  setConfig: (config: ClientConfig) => void;
  clear: () => void;
}

export const useClientConfigStore = create<ClientConfigState>()(
  persist(
    (set) => ({
      config: null,
      isLoaded: false,
      setConfig: (config) => set({ config, isLoaded: true }),
      clear: () => set({ config: null, isLoaded: false }),
    }),
    { name: 'tmf-client-config' },
  ),
);

// =============================================================================
// API
// =============================================================================

/**
 * Normaliza a resposta de GET /core/config/client.
 *
 * O backend responde em snake_case (Pydantic: `client_code`, `logo_url`...) e o
 * apiClient não converte case, então os campos camelCase de `ClientConfig`
 * chegavam sempre `undefined` (TMF9-66946). Só as chaves de topo são
 * convertidas: `features`/`settings` mantêm as chaves do backend
 * (ex.: `tmf7_base_url`, lido assim por useClientSetting).
 */
export function normalizeClientConfig(raw: unknown): ClientConfig {
  const r = (raw ?? {}) as Record<string, unknown>;
  const pick = <T>(camel: string, snake: string): T => (r[camel] ?? r[snake]) as T;
  return {
    clientCode: pick<string>('clientCode', 'client_code'),
    clientName: pick<string | null>('clientName', 'client_name') ?? null,
    logoUrl: pick<string | null>('logoUrl', 'logo_url') ?? null,
    primaryColor: pick<string | null>('primaryColor', 'primary_color') ?? null,
    secondaryColor: pick<string | null>('secondaryColor', 'secondary_color') ?? null,
    features: (r.features as Record<string, boolean>) ?? {},
    settings: (r.settings as Record<string, unknown>) ?? {},
    source: (r.source as ClientConfig['source']) ?? 'env',
  };
}

/**
 * Código do cliente/tenant informado pelo backend em /core/config/client
 * (`db_manager.active_client` ou `CLIENT_CODE` da instância), ou null se a
 * config ainda não foi carregada. Aceita também a forma snake_case que o store
 * persistido pode guardar de versões anteriores (TMF9-66946).
 */
export function clientCodeOf(config: ClientConfig | null | undefined): string | null {
  if (!config) return null;
  const code =
    config.clientCode ?? (config as unknown as { client_code?: string }).client_code;
  return code ? String(code) : null;
}

async function fetchClientConfig(): Promise<ClientConfig> {
  return normalizeClientConfig(await api.get<unknown>('/core/config/client'));
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook para acessar configurações do tenant.
 *
 * Usage:
 * ```tsx
 * const { config, isLoading } = useClientConfig();
 * const logo = config?.logoUrl;
 * const hasFeature = config?.features?.kanban ?? false;
 * ```
 */
export function useClientConfig(options?: { enabled?: boolean }) {
  const { config: cached, setConfig } = useClientConfigStore();
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: ['client-config'],
    queryFn: async () => {
      const data = await fetchClientConfig();
      setConfig(data);
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend Redis TTL)
    initialData: cached || undefined,
    enabled,
  });

  return {
    config: query.data || cached,
    isLoading: query.isLoading && !cached,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Helper para verificar feature flag do tenant.
 *
 * Usage:
 * ```tsx
 * const hasKanban = useFeatureFlag('kanban');
 * ```
 */
export function useFeatureFlag(flag: string): boolean {
  const { config } = useClientConfig();
  return config?.features?.[flag] ?? false;
}

/**
 * Helper para acessar setting do tenant.
 *
 * Usage:
 * ```tsx
 * const maxUpload = useClientSetting<number>('max_upload_mb', 10);
 * ```
 */
export function useClientSetting<T>(key: string, defaultValue: T): T {
  const { config } = useClientConfig();
  return (config?.settings?.[key] as T) ?? defaultValue;
}
